import React, { useEffect, useState, useCallback } from 'react'
import { supabase, AUTO_REFRESH_ENABLED } from '../supabaseClient'
import Calendar from './Calendar'
import Resources from './Resources'
import ClassScheduleView from './ClassScheduleView'
import { isMeetLinkVisible } from '../meetHelper'
import Layout, { PageHeader, Grid4, MetricCard, Panel, Lbl, Inp, Btn, Err, Ok, Empty, useTabs, Keep } from './Layout'
import ZoomRecordings from './ZoomRecordings'
import RescheduleManager from './RescheduleManager'
import EmergencyClass from './EmergencyClass'
import AttendanceManager from './AttendanceManager'
import SupportChat from './SupportChat'
import { AvailabilityGrid } from './TeacherAvailability'

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })
}

function fmtTime(t) {
  if (!t) return ''
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}

// ── STUDENT PROFILE MODAL ─────────────────────────────────────────────────────
function StudentProfileModal({ student, teacherClasses, onClose }) {
  const [attendance,     setAttendance]     = useState([])
  const [enrolledClasses,setEnrolledClasses] = useState([])
  const [resources,      setResources]      = useState([])
  const [payments,       setPayments]       = useState([])
  const [assignments,    setAssignments]    = useState([])
  const [activeTab,      setActiveTab]      = useState('overview')
  const [loading,        setLoading]        = useState(true)
  const [avatarUrl,      setAvatarUrl]      = useState(null)

  useEffect(() => {
    async function load() {
      setLoading(true)
      const classIds = teacherClasses.map(c => c.id)

      const [attRes, enrRes, resRes, payRes, asgRes, profRes] = await Promise.all([
        supabase.from('attendance').select('*').eq('student_id', student.id).in('class_id', classIds).order('date', { ascending: false }),
        supabase.from('enrollments').select('class_id').eq('student_id', student.id).in('class_id', classIds),
        supabase.from('resources').select('*').order('created_at', { ascending: false }).limit(20),
        supabase.from('payments').select('id,status,amount,currency,course_name,due_date,created_at').eq('student_id', student.id).order('created_at', { ascending: false }),
        supabase.from('assignments').select('*').eq('student_id', student.id).order('created_at', { ascending: false }).limit(30),
        supabase.from('profiles').select('avatar_url').eq('id', student.id).single(),
      ])

      setAttendance(attRes.data || [])
      const enrolledIds = (enrRes.data || []).map(e => e.class_id)
      setEnrolledClasses(teacherClasses.filter(c => enrolledIds.includes(c.id)))
      setResources(resRes.data || [])
      setPayments(payRes.data || [])
      setAssignments(asgRes.data || [])
      setAvatarUrl(profRes.data?.avatar_url || null)
      setLoading(false)
    }
    load()
  }, [student.id])

  // ── Stats ──
  const present        = attendance.filter(a => a.status === 'present').length
  const absent         = attendance.filter(a => a.status === 'absent').length
  const pct            = attendance.length ? Math.round((present / attendance.length) * 100) : 0
  const today          = new Date().toISOString().slice(0,10)
  const upcomingClasses = enrolledClasses.filter(c => c.class_date >= today)
  const pastClasses     = enrolledClasses.filter(c => c.class_date < today)
  const remaining       = upcomingClasses.length
  const completed       = pastClasses.length
  const totalEnrolled   = enrolledClasses.length

  // ── Payment stats ──
  const paidCount    = payments.filter(p => p.status === 'paid').length
  const pendingCount = payments.filter(p => p.status === 'pending' || p.status === 'overdue').length
  const paymentStatus = pendingCount > 0 ? 'pending' : paidCount > 0 ? 'paid' : 'none'

  // ── Assignment stats ──
  const submittedCount = assignments.filter(a => a.status === 'submitted' || a.status === 'completed').length
  const pendingAsgCount = assignments.filter(a => a.status === 'pending' || !a.status).length

  const tabStyle = (id) => ({
    fontSize: '12px', fontWeight: 700, padding: '7px 14px', borderRadius: '10px',
    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
    background: activeTab === id ? '#1e90ff' : 'rgba(var(--fg-rgb),0.06)',
    color: activeTab === id ? '#fff' : 'rgba(var(--fg-rgb),var(--a40))',
  })

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', overflowY:'auto' }}>
      <div style={{ background:'var(--bg-page)', border:'1px solid rgba(30,144,255,0.2)', borderRadius:'18px', padding:'24px', width:'100%', maxWidth:'720px', maxHeight:'92vh', overflowY:'auto' }}>

        {/* ── Header with photo ── */}
        <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'20px' }}>
          {/* Avatar */}
          <div style={{ width:'64px', height:'64px', borderRadius:'50%', flexShrink:0, overflow:'hidden', border:'2px solid rgba(30,144,255,0.3)', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt={student.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
            ) : (
              <span style={{ fontSize:'24px', fontWeight:800, color:'var(--fg-solid)' }}>{(student.full_name||'?').charAt(0).toUpperCase()}</span>
            )}
          </div>

          <div style={{ flex:1 }}>
            <div style={{ fontSize:'20px', fontWeight:800, color:'var(--fg-solid)' }}>{student.full_name || '—'}</div>
            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'3px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
              {student.student_id && <span>🎓 ID: {student.student_id}</span>}
              <span>📅 Joined {fmtDate(student.created_at)}</span>
              {student.email && <span>✉ {student.email}</span>}
            </div>
            {/* Payment badge */}
            <div style={{ marginTop:'6px' }}>
              {paymentStatus === 'paid' && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 10px', borderRadius:'10px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.3)' }}>✅ Fees Paid</span>}
              {paymentStatus === 'pending' && <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 10px', borderRadius:'10px', background:'rgba(244,163,53,0.15)', color:'#f4a335', border:'0.5px solid rgba(244,163,53,0.3)' }}>⚠ Fee Pending</span>}
            </div>
          </div>

          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'26px', cursor:'pointer', flexShrink:0 }}>×</button>
        </div>

        {/* ── Stats grid ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(80px,1fr))', gap:'8px', marginBottom:'20px' }}>
          {[
            { label:'Total',     value: totalEnrolled, color:'#5aabff'  },
            { label:'Remaining', value: remaining,     color:'#a78bfa'  },
            { label:'Completed', value: completed,     color:'#34d399'  },
            { label:'Attended',  value: present,       color:'#10b981'  },
            { label:'Rate',      value: `${pct}%`,     color: pct >= 75 ? '#34d399' : pct >= 50 ? '#f4a335' : '#f87171' },
          ].map(s => (
            <div key={s.label} style={{ background:'rgba(var(--fg-rgb),0.04)', borderRadius:'12px', padding:'12px 8px', textAlign:'center', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
              <div style={{ fontSize:'20px', fontWeight:800, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'3px', textTransform:'uppercase', letterSpacing:'0.06em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' }}>
          {[
            { id:'overview',    label:'📋 Overview'    },
            { id:'classes',     label:'📅 Classes'     },
            { id:'attendance',  label:'✅ Attendance'   },
            { id:'payments',    label:`💳 Payments${pendingCount > 0 ? ` (${pendingCount})` : ''}`  },
            { id:'assignments', label:`📝 Assignments${pendingAsgCount > 0 ? ` (${pendingAsgCount})` : ''}` },
            { id:'resources',   label:'📚 Resources'   },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabStyle(t.id)}>{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))' }}>Loading...</div>
        ) : (
          <>
            {/* ── OVERVIEW ── */}
            {activeTab === 'overview' && (
              <div style={{ display:'grid', gap:'10px' }}>
                <div style={{ padding:'14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                  <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Student Info</div>
                  {[
                    { label:'Full Name',        value: student.full_name },
                    { label:'Student ID',       value: student.student_id || '—' },
                    { label:'Email',            value: student.email || '—' },
                    { label:'Timezone',         value: student.timezone || '—' },
                    { label:'Joined',           value: fmtDate(student.created_at) },
                    { label:'Total Classes',    value: totalEnrolled },
                    { label:'Classes Remaining',value: remaining },
                    { label:'Classes Done',     value: completed },
                    { label:'Attendance Rate',  value: `${pct}% (${present}/${attendance.length})` },
                    { label:'Payment Status',   value: paymentStatus === 'paid' ? '✅ All Paid' : paymentStatus === 'pending' ? `⚠ ${pendingCount} Pending` : '— No records' },
                    { label:'Assignments',      value: `${submittedCount} submitted · ${pendingAsgCount} pending` },
                  ].map(row => (
                    <div key={row.label} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'0.5px solid rgba(var(--fg-rgb),0.04)' }}>
                      <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a35))' }}>{row.label}</span>
                      <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a80))', fontWeight:600 }}>{row.value}</span>
                    </div>
                  ))}
                </div>

                {/* Next class card */}
                {upcomingClasses.length > 0 && (
                  <div style={{ padding:'14px', background:'rgba(30,144,255,0.07)', borderRadius:'12px', border:'0.5px solid rgba(30,144,255,0.2)' }}>
                    <div style={{ fontSize:'11px', color:'#5aabff', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Next Class</div>
                    <div style={{ fontSize:'14px', fontWeight:700, color:'var(--fg-solid)' }}>{upcomingClasses[0].title}</div>
                    <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'4px' }}>
                      📅 {fmtDate(upcomingClasses[0].class_date)}
                      {upcomingClasses[0].start_time && ` · ⏰ ${fmtTime(upcomingClasses[0].start_time)}`}
                    </div>
                    {upcomingClasses[0].meet_link && (
                      isMeetLinkVisible(upcomingClasses[0].class_date, upcomingClasses[0].start_time) ? (
                        <a href={upcomingClasses[0].meet_link} target="_blank" rel="noreferrer"
                          style={{ display:'inline-flex', marginTop:'8px', fontSize:'12px', fontWeight:700, padding:'6px 14px', borderRadius:'8px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.3)', textDecoration:'none' }}>
                          🔗 Join Class
                        </a>
                      ) : (
                        <div style={{ display:'inline-flex', marginTop:'8px', fontSize:'11px', fontStyle:'italic', padding:'6px 14px', borderRadius:'8px', background:'rgba(var(--fg-rgb),0.04)', color:'rgba(var(--fg-rgb),var(--a30))' }}>
                          🔒 Join link visible 24h before class
                        </div>
                      )
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── CLASSES ── */}
            {activeTab === 'classes' && (
              <div>
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
                  {[
                    { label:`Upcoming (${upcomingClasses.length})`, color:'#5aabff',  bg:'rgba(30,144,255,0.1)'  },
                    { label:`Completed (${completed})`,             color:'#34d399',  bg:'rgba(16,185,129,0.1)'  },
                    { label:`Remaining (${remaining})`,             color:'#a78bfa',  bg:'rgba(139,92,246,0.1)'  },
                  ].map(s => (
                    <div key={s.label} style={{ padding:'6px 14px', borderRadius:'20px', background:s.bg, fontSize:'12px', fontWeight:700, color:s.color }}>{s.label}</div>
                  ))}
                </div>
                <div style={{ display:'grid', gap:'6px' }}>
                  {enrolledClasses.length === 0 ? (
                    <Empty msg="No classes enrolled." />
                  ) : (
                    [...enrolledClasses]
                      .sort((a,b) => a.class_date.localeCompare(b.class_date) || (a.start_time||'').localeCompare(b.start_time||''))
                      .map((c, idx) => {
                        const isPast = c.class_date < today
                        return (
                          <div key={c.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 14px', background: isPast ? 'rgba(var(--fg-rgb),0.03)' : 'rgba(30,144,255,0.07)', borderRadius:'10px', border:`0.5px solid ${isPast ? 'rgba(var(--fg-rgb),0.06)' : 'rgba(30,144,255,0.2)'}`, opacity: isPast ? 0.7 : 1 }}>
                            <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 7px', borderRadius:'6px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a40))', flexShrink:0 }}>#{idx+1}</span>
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{c.title}</div>
                              <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>
                                📅 {fmtDate(c.class_date)} {c.start_time && `· ⏰ ${fmtTime(c.start_time)}`}
                              </div>
                            </div>
                            {isPast
                              ? <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(16,185,129,0.1)', color:'#34d399' }}>✓ Done</span>
                              : <span style={{ fontSize:'9px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background:'rgba(30,144,255,0.15)', color:'#5aabff' }}>Upcoming</span>
                            }
                            {!isPast && c.meet_link && (
                              isMeetLinkVisible(c.class_date, c.start_time) ? (
                                <a href={c.meet_link} target="_blank" rel="noreferrer"
                                  style={{ fontSize:'10px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.3)', textDecoration:'none' }}>
                                  🔗 Join
                                </a>
                              ) : (
                                <span style={{ fontSize:'10px', fontStyle:'italic', color:'rgba(var(--fg-rgb),var(--a25))' }}>🔒 24h before</span>
                              )
                            )}
                          </div>
                        )
                      })
                  )}
                </div>
              </div>
            )}

            {/* ── ATTENDANCE ── */}
            {activeTab === 'attendance' && (
              <div>
                {/* Attendance bar */}
                <div style={{ marginBottom:'14px', padding:'12px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'8px' }}>
                    <span style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a50))' }}>Attendance Rate</span>
                    <span style={{ fontSize:'14px', fontWeight:800, color: pct >= 75 ? '#34d399' : pct >= 50 ? '#f4a335' : '#f87171' }}>{pct}%</span>
                  </div>
                  <div style={{ height:'6px', borderRadius:'3px', background:'rgba(var(--fg-rgb),0.06)', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background: pct >= 75 ? '#34d399' : pct >= 50 ? '#f4a335' : '#f87171', borderRadius:'3px', transition:'width 0.5s ease' }}/>
                  </div>
                  <div style={{ display:'flex', gap:'16px', marginTop:'8px', fontSize:'11px' }}>
                    <span style={{ color:'#34d399' }}>✅ Present: {present}</span>
                    <span style={{ color:'#f87171' }}>❌ Absent: {absent}</span>
                    <span style={{ color:'rgba(var(--fg-rgb),var(--a30))' }}>Total: {attendance.length}</span>
                  </div>
                </div>
                <div style={{ display:'grid', gap:'6px' }}>
                  {attendance.length === 0 ? (
                    <Empty msg="No attendance records yet." />
                  ) : attendance.map(a => (
                    <div key={a.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'9px 12px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'9px', border:'0.5px solid rgba(var(--fg-rgb),0.06)' }}>
                      <span style={{ fontSize:'16px' }}>{a.status === 'present' ? '✅' : '❌'}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a75))' }}>
                          {teacherClasses.find(c => c.id === a.class_id)?.title || 'Class'}
                        </div>
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'1px' }}>{fmtDate(a.date || a.created_at?.slice(0,10))}</div>
                      </div>
                      <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background: a.status === 'present' ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: a.status === 'present' ? '#34d399' : '#f87171' }}>
                        {a.status === 'present' ? 'Present' : 'Absent'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── PAYMENTS ── */}
            {activeTab === 'payments' && (
              <div>
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
                  <div style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(16,185,129,0.1)', fontSize:'12px', fontWeight:700, color:'#34d399' }}>✅ Paid: {paidCount}</div>
                  <div style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(244,163,53,0.1)', fontSize:'12px', fontWeight:700, color:'#f4a335' }}>⚠ Pending: {pendingCount}</div>
                </div>
                {payments.length === 0 ? (
                  <Empty msg="No payment records." />
                ) : (
                  <div style={{ display:'grid', gap:'6px' }}>
                    {payments.map(p => (
                      <div key={p.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'11px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px', borderLeft:`3px solid ${p.status==='paid'?'#34d399':p.status==='overdue'?'#f87171':'#f4a335'}` }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{p.course_name || 'Course Fee'}</div>
                          {p.due_date && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>Due: {fmtDate(p.due_date)}</div>}
                        </div>
                        <span style={{ fontSize:'10px', fontWeight:700, padding:'4px 10px', borderRadius:'8px', background: p.status==='paid'?'rgba(16,185,129,0.15)':p.status==='overdue'?'rgba(239,68,68,0.15)':'rgba(244,163,53,0.15)', color: p.status==='paid'?'#34d399':p.status==='overdue'?'#f87171':'#f4a335', textTransform:'uppercase' }}>
                          {p.status}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── ASSIGNMENTS ── */}
            {activeTab === 'assignments' && (
              <div>
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
                  <div style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(16,185,129,0.1)', fontSize:'12px', fontWeight:700, color:'#34d399' }}>✅ Submitted: {submittedCount}</div>
                  <div style={{ padding:'6px 14px', borderRadius:'20px', background:'rgba(244,163,53,0.1)', fontSize:'12px', fontWeight:700, color:'#f4a335' }}>⏳ Pending: {pendingAsgCount}</div>
                </div>
                {assignments.length === 0 ? (
                  <div style={{ textAlign:'center', padding:'2rem', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>
                    No assignments yet. Assignments will appear here once created.
                  </div>
                ) : (
                  <div style={{ display:'grid', gap:'6px' }}>
                    {assignments.map(a => (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'11px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                        <span style={{ fontSize:'20px', flexShrink:0 }}>{a.status === 'submitted' || a.status === 'completed' ? '✅' : '⏳'}</span>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.title || 'Assignment'}</div>
                          <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>
                            {a.due_date && `Due: ${fmtDate(a.due_date)}`}
                            {a.submitted_at && ` · Submitted: ${fmtDate(a.submitted_at?.slice(0,10))}`}
                          </div>
                        </div>
                        <span style={{ fontSize:'10px', fontWeight:700, padding:'3px 8px', borderRadius:'6px', background: (a.status==='submitted'||a.status==='completed') ? 'rgba(16,185,129,0.12)' : 'rgba(244,163,53,0.12)', color: (a.status==='submitted'||a.status==='completed') ? '#34d399' : '#f4a335', textTransform:'uppercase', flexShrink:0 }}>
                          {a.status || 'Pending'}
                        </span>
                        {a.file_url && (
                          <a href={a.file_url} target="_blank" rel="noreferrer"
                            style={{ fontSize:'10px', fontWeight:700, padding:'4px 10px', borderRadius:'7px', background:'rgba(30,144,255,0.12)', color:'#5aabff', textDecoration:'none', flexShrink:0 }}>
                            View
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── RESOURCES ── */}
            {activeTab === 'resources' && (
              <div style={{ display:'grid', gap:'8px' }}>
                {resources.length === 0 ? (
                  <Empty msg="No resources available." />
                ) : resources.map(r => (
                  <div key={r.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'11px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'10px', border:'0.5px solid rgba(var(--fg-rgb),0.06)' }}>
                    <span style={{ fontSize:'20px' }}>{r.type === 'video' ? '🎥' : r.type === 'pdf' ? '📄' : '🔗'}</span>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.title}</div>
                      {r.description && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>{r.description}</div>}
                    </div>
                    {r.url && (
                      <a href={r.url} target="_blank" rel="noreferrer"
                        style={{ fontSize:'11px', fontWeight:700, padding:'5px 12px', borderRadius:'8px', background:'rgba(30,144,255,0.12)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.2)', textDecoration:'none', whiteSpace:'nowrap' }}>
                        Open →
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── MAIN TEACHER DASH ─────────────────────────────────────────────────────────
export default function TeacherDash({ profile }) {
  const { tab, go: setTab, has: visited } = useTabs('classes')
  const [classes,  setClasses]  = useState([])
  const [students, setStudents] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [meetLink, setMeetLink] = useState('')
  const [editId,   setEditId]   = useState(null)
  const [busy,     setBusy]     = useState(false)
  const [err,      setErr]      = useState('')
  const [ok,       setOk]       = useState('')
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [searchStudents,  setSearchStudents]  = useState('')

  const today        = new Date().toISOString().slice(0,10)
  const upcoming     = classes.filter(c => c.class_date >= today)
  const done         = classes.filter(c => c.class_date <  today)
  const todayClasses = classes.filter(c => c.class_date === today)

  const loadClasses = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('classes').select('*')
      .eq('teacher_id', profile.id)
      .neq('is_cancelled', true)
      .order('class_date', { ascending: true })
      .order('start_time', { ascending: true })

    const classList = data || []

    // ── Attach enrolled student names so they show up in front of each
    // class in the schedule view, not just inside the expandable roster.
    if (classList.length) {
      const classIds = classList.map(c => c.id)
      const { data: enr } = await supabase.from('enrollments').select('class_id,student_id').in('class_id', classIds)
      const studentIds = [...new Set((enr||[]).map(e => e.student_id).filter(Boolean))]
      const { data: students } = studentIds.length
        ? await supabase.from('profiles').select('id,full_name').in('id', studentIds)
        : { data: [] }
      const nameById = new Map((students||[]).map(s => [s.id, s.full_name]))
      const namesByClass = new Map()
      for (const e of (enr||[])) {
        const name = nameById.get(e.student_id)
        if (!name) continue
        const list = namesByClass.get(e.class_id) || []
        list.push(name)
        namesByClass.set(e.class_id, list)
      }
      classList.forEach(c => { c.student_names = (namesByClass.get(c.id) || []).join(', ') })
    }

    setClasses(classList)
    setLoading(false)
  }, [profile.id])

  useEffect(() => { loadClasses() }, [loadClasses])

  useEffect(() => {
    if (tab === 'students') loadMyStudents()
  }, [tab, classes])

  useEffect(() => {
    if (!AUTO_REFRESH_ENABLED) return
    const ch = supabase.channel('teacher-classes-rt')
      .on('postgres_changes', { event:'*', schema:'public', table:'classes', filter:`teacher_id=eq.${profile.id}` }, () => loadClasses())
      .subscribe()
    return () => supabase.removeChannel(ch)
  }, [profile.id, loadClasses])

  async function loadMyStudents() {
    if (!classes.length) { setStudents([]); return }
    const classIds = classes.map(c => c.id)
    const { data: enrollments } = await supabase.from('enrollments').select('student_id').in('class_id', classIds)
    let ids = [...new Set((enrollments||[]).map(e => e.student_id))]
    if (!ids.length) {
      const { data: att } = await supabase.from('attendance').select('student_id').in('class_id', classIds)
      ids = [...new Set((att||[]).map(a => a.student_id))]
    }
    if (!ids.length) { setStudents([]); return }
    const { data: s } = await supabase.from('profiles')
      .select('id,full_name,email,created_at,student_id,timezone,avatar_url')
      .in('id', ids)
      .order('full_name')
    setStudents(s || [])
  }

  function markJoined(classId) {
    // Fire-and-forget — feeds the no-show check in api/send-class-reminders.js.
    // Doesn't block the join link opening in a new tab.
    supabase.from('classes').update({ teacher_joined_at: new Date().toISOString() }).eq('id', classId).then(() => {})
  }

  async function updateZoomLink(e) {
    e?.preventDefault(); setErr(''); setOk('')
    if (!editId) return
    setBusy(true)
    const { error } = await supabase.from('classes').update({ meet_link: meetLink.trim() || null }).eq('id', editId).eq('teacher_id', profile.id)
    if (error) setErr(error.message)
    else { setOk('✓ Meeting link updated!'); setMeetLink(''); setEditId(null); loadClasses() }
    setBusy(false)
  }

  function startEditZoom(cls) {
    setEditId(cls.id); setMeetLink(cls.meet_link || ''); setErr(''); setOk('')
    setTimeout(() => document.getElementById('zoom-edit-panel')?.scrollIntoView({ behavior:'smooth', block:'nearest' }), 100)
  }

  const firstName = (profile.full_name || profile.email).split(/[ @]/)[0]

  const filteredStudents = students.filter(s =>
    !searchStudents ||
    s.full_name?.toLowerCase().includes(searchStudents.toLowerCase()) ||
    s.student_id?.toLowerCase().includes(searchStudents.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchStudents.toLowerCase())
  )

  return (
    <Layout profile={profile} activeTab={tab} onTabChange={setTab}>

      {visited('classes') && (
        <Keep show={tab === 'classes'}>
          <PageHeader title={`Welcome back, ${firstName} 👋`} subtitle="Your class schedule — sorted by date & time." />
          <Grid4>
            <MetricCard icon="📅" label="Upcoming"    value={upcoming.length}  />
            <MetricCard icon="🔴" label="Today"       value={todayClasses.length} accentColor={todayClasses.length>0?'#f87171':undefined}/>
            <MetricCard icon="✅" label="Completed"   value={done.length}      />
            <MetricCard icon="👥" label="My Students" value={students.length || '—'} />
          </Grid4>

          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px', flexWrap:'wrap' }}>
            <div style={{ flex:1, padding:'10px 14px', background:'rgba(30,144,255,0.07)', border:'0.5px solid rgba(30,144,255,0.18)', borderRadius:'10px', fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a45))', lineHeight:1.6 }}>
              📋 Classes assigned by Admin. Click <strong style={{color:'#5aabff'}}>✏ Link</strong> on any class to add your meeting link.
            </div>
            <EmergencyClass profile={profile} myStudents={students} onClassCreated={loadClasses}/>
          </div>

          {editId && (
            <div id="zoom-edit-panel" style={{ marginBottom:'14px', padding:'16px', background:'rgba(30,144,255,0.07)', border:'0.5px solid rgba(30,144,255,0.2)', borderRadius:'12px' }}>
              <div style={{ fontSize:'13px', color:'#5aabff', fontWeight:700, marginBottom:'10px' }}>
                ✏ Update Zoom Link — {classes.find(c=>c.id===editId)?.title}
              </div>
              <Err msg={err}/><Ok msg={ok}/>
              <form onSubmit={updateZoomLink} style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                <Inp type="url" placeholder="https://meet.google.com/abc-defg-hij" value={meetLink} onChange={e=>setMeetLink(e.target.value)} style={{ flex:1, margin:0 }}/>
                <Btn busy={busy} style={{ width:'auto', padding:'0 18px', margin:0 }}>Save</Btn>
                <button type="button" onClick={()=>setEditId(null)} style={{ padding:'9px 14px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a40))', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit', fontSize:'13px' }}>Cancel</button>
              </form>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading your classes...</div>
          ) : (
            <ClassScheduleView classes={classes} onEditZoom={startEditZoom} showTeacher={false} role="teacher" onJoin={cls => markJoined(cls.id)} defaultView="calendar"/>
          )}
        </Keep>
      )}

      {visited('students') && (
        <Keep show={tab === 'students'}>
          <PageHeader title="My Students" subtitle={`${students.length} students — click to view full profile`} />
          <div style={{ marginBottom:'12px' }}>
            <input
              type="text"
              placeholder="🔍 Search by name, ID or email..."
              value={searchStudents}
              onChange={e => setSearchStudents(e.target.value)}
              style={{ width:'100%', background:'rgba(var(--fg-rgb),0.05)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px 14px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', boxSizing:'border-box', colorScheme:'dark' }}
            />
          </div>
          <Panel>
            {students.length === 0 ? (
              <div style={{ textAlign:'center', padding:'2.5rem' }}>
                <div style={{ fontSize:'32px', marginBottom:'10px' }}>👥</div>
                <div style={{ fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a30))' }}>No students assigned yet.</div>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a30))', fontSize:'13px' }}>No students match your search.</div>
            ) : (
              <div style={{ display:'grid', gap:'8px' }}>
                {filteredStudents.map(s => (
                  <div key={s.id} onClick={() => setSelectedStudent(s)}
                    style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 14px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', border:'0.5px solid rgba(var(--fg-rgb),0.07)', cursor:'pointer', transition:'all 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(30,144,255,0.08)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(var(--fg-rgb),0.03)'}>
                    {/* Avatar */}
                    <div style={{ width:'42px', height:'42px', borderRadius:'50%', flexShrink:0, overflow:'hidden', background:'linear-gradient(135deg,rgba(16,185,129,0.3),rgba(30,144,255,0.3))', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {s.avatar_url ? (
                        <img src={s.avatar_url} alt={s.full_name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                      ) : (
                        <span style={{ color:'#34d399', fontSize:'16px', fontWeight:800 }}>{(s.full_name||s.email||'?').charAt(0).toUpperCase()}</span>
                      )}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'14px', color:'rgba(var(--fg-rgb),var(--a90))', fontWeight:600 }}>{s.full_name || '—'}</div>
                      <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>
                        {s.student_id && <span>🎓 {s.student_id} · </span>}
                        {s.email && <span>✉ {s.email}</span>}
                      </div>
                    </div>
                    <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a20))', flexShrink:0 }}>View Profile →</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </Keep>
      )}

      {visited('attendance') && <Keep show={tab === 'attendance'}><PageHeader title="Attendance" subtitle="Mark attendance for your classes."/><AttendanceManager profile={profile} teacherClasses={classes}/></Keep>}
      {visited('calendar')   && <Keep show={tab === 'calendar'}><PageHeader title="Calendar"/><Calendar profile={profile}/></Keep>}
      {visited('resources')  && <Keep show={tab === 'resources'}><PageHeader title="Resources"/><Resources profile={profile}/></Keep>}
      {visited('support')    && <Keep show={tab === 'support'}><PageHeader title="Support"/><SupportChat profile={profile}/></Keep>}
      {visited('recordings') && <Keep show={tab === 'recordings'}><PageHeader title="Recordings"/><ZoomRecordings profile={profile}/></Keep>}
      {visited('reschedule') && <Keep show={tab === 'reschedule'}><PageHeader title="Reschedule"/><RescheduleManager profile={profile}/></Keep>}
      {visited('availability') && <Keep show={tab === 'availability'}><PageHeader title="My Availability" subtitle="Your weekly working hours, set by admin."/><AvailabilityGrid teacherId={profile.id} readOnly/></Keep>}

      {selectedStudent && (
        <StudentProfileModal
          student={selectedStudent}
          teacherClasses={classes}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </Layout>
  )
}
